import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { collections } from "@/mocks/meta";
import { ArticleCard } from "@/components/feature/ArticleCards";

export default function CollectionPage() {
  const { slug = "" } = useParams();
  const col = collections.find((c) => c.slug === slug);

  useSeo(
    col ? `吃瓜精选专题｜${col.title}` : "专题不存在",
    col ? `${col.title}：${col.subtitle}，吃瓜精选按主题汇总、脉络完整的专题合集。` : "该专题不存在。"
  );

  if (!col) {
    return (
      <div className="wrap flex min-h-[60vh] flex-col items-center justify-center pt-16 text-center">
        <i className="ri-bookmark-line text-5xl text-secondary-400" />
        <h1 className="serif mt-4 text-2xl font-bold text-foreground-950">找不到这个专题</h1>
        <Link to="/" className="mt-5 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">返回首页</Link>
      </div>
    );
  }

  const related = collections.filter((c) => c.slug !== col.slug);
  const included = col.articleSlugs.map((s) => articles.find((a) => a.slug === s)).filter(Boolean) as typeof articles;

  return (
    <div className="pt-16 md:pt-[72px]">
      {/* hero */}
      <section className="relative overflow-hidden bg-foreground-900">
        <div className="absolute inset-0">
          <img src={col.cover} alt={`${col.title}专题封面`} className="h-full w-full object-cover object-center opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/30" />
        </div>
        <div className="wrap relative flex min-h-[52vh] flex-col justify-end pb-10 pt-24 md:pb-14">
          <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-background-300" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">专题合集</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-300">{col.title}</span>
          </nav>
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
            <i className="ri-flashlight-fill" /> 专题合集 · {col.meta}
          </span>
          <h1 className="serif mt-4 max-w-3xl text-2xl font-black leading-tight text-background-50 md:text-[2.6rem] md:leading-[1.15]">{col.title}</h1>
          <p className="mt-2 text-base text-accent-200 md:text-lg">{col.subtitle}</p>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-[15px]">{col.intro}</p>
        </div>
      </section>

      {/* timeline */}
      <section className="wrap py-12 md:py-16" aria-label="专题脉络">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow"><span className="eyebrow-line" />章节脉络</p>
            <h2 className="serif mt-3 text-2xl font-bold leading-tight text-foreground-950">顺着时间线，一次看透</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">本专题按时间与因果组织成若干章节，从起因读到落幕，脉络完整、不再碎片。</p>
            <div className="mt-6 rounded-xl border border-background-200/70 bg-background-100/50 p-5">
              <p className="text-sm font-semibold text-foreground-900">收录盘点</p>
              <p className="mt-1 text-3xl font-bold text-primary-600">{included.length}<span className="text-sm font-medium text-secondary-500"> 篇</span></p>
              <p className="mt-2 text-xs leading-relaxed text-secondary-600">跨栏目内容，已按主题整理进本专题统一呈现。</p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <ol className="relative space-y-0 border-l border-background-200 pl-6 md:pl-8">
              {col.chapters.map((ch, i) => (
                <li key={ch.title} className="relative pb-8 last:pb-0">
                  <span className="absolute -left-[31px] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50 md:-left-[39px]">
                    {i + 1}
                  </span>
                  <h3 className="serif text-lg font-bold text-foreground-950">{ch.title}</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{ch.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* included articles */}
      <section className="border-t border-background-200/70 bg-background-100/60 py-12 md:py-16" aria-label="专题收录内容">
        <div className="wrap">
          <h2 className="serif text-2xl font-bold text-foreground-950">本专题收录的完整盘点</h2>
          <p className="mt-2 text-sm text-secondary-600">点击即可展开每一篇的完整脉络。</p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {included.map((a) => (
              <ArticleCard key={a.slug} a={a} compact />
            ))}
          </div>
        </div>
      </section>

      {/* related */}
      {related.length > 0 && (
        <section className="wrap py-12 md:py-16" aria-label="相关专题">
          <h2 className="serif text-xl font-bold text-foreground-950">更多专题合集</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
            {related.map((c) => (
              <Link key={c.slug} to={`/collection/${c.slug}`} className="group flex items-center gap-4 overflow-hidden rounded-xl border border-background-200/70 bg-background-100/40 p-3 transition-colors hover:border-accent-300/70 md:p-4">
                <img src={c.cover} alt={c.title} className="h-24 w-28 flex-shrink-0 rounded-lg object-cover object-center transition-transform duration-500 group-hover:scale-[1.03] md:w-36" />
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-primary-700">{c.meta}</span>
                  <h3 className="serif mt-1 line-clamp-2 text-base font-bold leading-snug text-foreground-950">{c.title}</h3>
                  <p className="mt-1 hidden text-xs text-secondary-600 md:line-clamp-1">{c.subtitle}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}